import { Button, List, Modal, Col, Tag, Row, Card, Space, Tooltip, Typography, Divider, Input } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DownloadOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { getTemplates } from "../../../../../services/templates";
import { ActionType } from "../../../../Trigger-templates";
import { getCalendarIntegration } from "../../../../../services/calendar-integration";
import { getChatInstance } from "../../../../../services/chatIntegration";
import { getCatalogIntegration } from "../../../../../services/catalog-integration";
import { Link } from "react-router-dom";
import { createGenerativeTriggers } from "../../../../../services/generative-triggers";
import useDebounce from "../../../../../hooks/useDebounce";
import { toast } from 'sonner'

export default function AddTemplateModal({ refresh, open, setOpen, assistantId, chatbotId }) {
  const [loading, setLoading] = useState(false)
  const [templates, setTemplates] = useState()
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearchTerm = useDebounce(search, 500);
  const [total, setTotal] = useState(0);
  const [calendarIntegrationExists, setCalendarIntegrationExists] = useState(false);
  const [chatIntegrationExists, setChatIntegrationExists] = useState(false);
  const [catalogIntegrationExists, setCatalogIntegrationExists] = useState(false);
  const prevSearchTermRef = useRef(search);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getCalendarIntegration({ assistantID: assistantId }).then(({ data }) => {
        setCalendarIntegrationExists(data.length > 0);
      }),
      getChatInstance({ assistantID: assistantId, chatbotID: chatbotId }).then(({ data }) => {
        setChatIntegrationExists(data.length > 0);
      }),
      getCatalogIntegration(assistantId).then(({ data }) => {
        setCatalogIntegrationExists(data.length > 0);
      }),
    ]).finally(() => setLoading(false));
  }, [assistantId, chatbotId]);

  const getTemplateList = useCallback((currentPage, currentPerPage, currentSearch) => {
    setLoading(true);
    const params = {
      page: currentPage,
      perPage: currentPerPage,
      search: currentSearch,
      searchField: 'name'
    };

    getTemplates(params)
      .then(({ data, total }) => {
        setTemplates(data);
        setTotal(total);
      })
      .catch((err) => {
        toast.error("Não foi possível carregar templates");
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    const isNewSearch = prevSearchTermRef.current !== debouncedSearchTerm;

    if (isNewSearch) {
      prevSearchTermRef.current = debouncedSearchTerm;
      setPage(1);
    }

    if (isNewSearch && page !== 1) {
      return;
    }

    getTemplateList(page, perPage, debouncedSearchTerm);
  }, [getTemplateList, open, page, perPage, debouncedSearchTerm]);

  return (
    <Modal width={'56%'} onOk={() => setOpen(false)} open={open} onCancel={() => setOpen(false)} cancelText="Cancelar">
        <Row justify={'space-between'} style={{marginTop: '28px'}}>
          <Typography.Title level={3}>Adicionar Gatilho via Template</Typography.Title>
          <Space direction={'horizontal'}>
            <Input.Search placeholder={'Pesquisa por nome'}
              onSearch={(search) => setSearch(search)}
              onChange={({ target }) => setSearch(target.value)} />
          </Space>
        </Row>

      <Divider />

      <List
        style={{ padding: 20 }}
        loading={loading}
        dataSource={templates}
        pagination={{
          position: "bottom",
          align: "end",
          onChange: (page, perPage) => {
            setPage(page);
            setPerPage(perPage);
          },
          total,
          pageSize: perPage,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          current: page,
        }}
        renderItem={(item, key) => (
          <AddTemplateItem
            refresh={refresh}
            key={key}
            item={item}
            assistantId={assistantId}
            calendarIntegrationExists={calendarIntegrationExists}
            chatIntegrationExists={chatIntegrationExists}
            catalogIntegrationExists={catalogIntegrationExists}
          />
        )}
      />
    </Modal>
  );
}

const AddTemplateItem = ({
  item,
  assistantId,
  calendarIntegrationExists,
  chatIntegrationExists,
  catalogIntegrationExists,
  refresh
}) => {
  const [loading, setLoading] = useState(false)

  const integrationTypes = useMemo(
    () => item.generativeActionTemplates?.map((action) => action.type) || [],
    [item.generativeActionTemplates]
  );

  const hasIntegration = useMemo(() => {
    return integrationTypes.every((type) => {
      if (type === "GOOGLE_CALENDAR") return calendarIntegrationExists;
      if (type === "CHAT_INTEGRATION") return chatIntegrationExists;
      if (type === "CATALOG_INTEGRATION") return catalogIntegrationExists;
      return true;
    });
  }, [
    integrationTypes,
    calendarIntegrationExists,
    chatIntegrationExists,
    catalogIntegrationExists,
  ]);

  const InstallIntegrationButton = () =>
    <Link to={`/assistants/${assistantId}/integrations`}>
      <Button
        icon={<DownloadOutlined />}
        type="primary"
        key="criar">
        Instalar Integração
      </Button>
    </Link>

  const addTemplate = useCallback(async () => {
    setLoading(true)

    await createGenerativeTriggers(assistantId, { generativeTriggerTemplateID: item.id })
      .then(() => {
        toast.success("Template adicionado com sucesso!");
        refresh();
      })
      .catch(error => {
        toast.error("Houve um erro ao adicionar o template");
        console.error(error);
      })
      .finally(() => setLoading(false))

  }, [assistantId, item, refresh]);

  const AddTemplateButton = () =>
    <Button
      loading={loading}
      onClick={addTemplate}
      icon={<PlusOutlined />}
      type="primary"
      key="criar">
      Adicionar
    </Button>;

  return (
    <List.Item actions={[!hasIntegration ? <InstallIntegrationButton /> : <AddTemplateButton />]}>
      <List.Item.Meta
        title={<Typography.Title level={4}>{item.name}</Typography.Title>}
        description={
          <>
            <Typography.Paragraph>{item.description}</Typography.Paragraph>
            <div style={{ marginBottom: 8 }}>
              {item?.args?.parameters?.properties &&
                Object.keys(item.args.parameters.properties).map((prop, index) => (
                  <Tag key={index} style={{ marginBottom: 4 }}>{prop}</Tag>
                ))}
            </div>
            <div>
              {item?.generativeActionTemplates?.map((action, index) => (
                <span key={index} style={{ display: 'inline-block', marginRight: 8 }}>
                  <ActionType type={action.type} />
                </span>
              ))}
            </div>
          </>
        }
      />
    </List.Item>
  )
}
